import { faSearch, faXmark } from '@fortawesome/pro-light-svg-icons'
import { InputWithRightIcon } from '../../forms/Input'
import { ChangeValueFunc } from '../../utils/globals/types'
import Checkbox from '../../forms/Checkbox'
import { useDebounce } from 'use-debounce'
import { useCallback, useEffect, useState } from 'react'

interface SearchBarProps {
    onChange: ChangeValueFunc
    searchValue: string
    className?: string
    placeholder?: string
    onFocus?: () => void
    myref?: React.MutableRefObject<any>
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
    onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>
}

const SearchBar: React.FC<SearchBarProps> = ({ searchValue, className, onChange, onKeyDown, ...rest }) => {
    const { searchIcon, resetSearchIcon } = useResetSearchValueLogic({searchValue, onChange})
    return (
        <div className={' light14 searchbar ' + (className || '')} onClick={(e) => e.stopPropagation()}>
            <InputWithRightIcon className='placeholder-gray-97' iconStyles={{ height: '16px', width: '16px', color: '#636363' }} icon={searchIcon} value={searchValue} onChange={onChange} onClickResetIcon={resetSearchIcon} onKeyDown={onKeyDown} {...rest} />
        </div>
    )
}
interface SearchBarWithDebounceProps extends SearchBarWithDebounceLogicProps {
    className?: string
    placeholder?: string
    onFocus?: () => void
    onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>
}

export const SearchBarWithDebounce: React.FC<SearchBarWithDebounceProps> = ({ className, searchValue, onChange, ...rest }) => {
    const { mySearchValue, setMySearchValue, searchIcon, resetSearchIcon } = useSearchBarWithDebounceLogic({ searchValue, onChange })
    return (
        <div className={' light14 searchbar ' + (className || '')} onClick={(e) => e.stopPropagation()}>
            <InputWithRightIcon className='placeholder-gray-97 w-full ' iconStyles={{ height: '16px', width: '16px', color: '#636363' }} icon={searchIcon} value={mySearchValue} onChange={setMySearchValue} onClickResetIcon={resetSearchIcon} {...rest} />
        </div>
    )
}

interface SearchBarWithDebounceLogicProps {
    onChange: ChangeValueFunc
    searchValue: string
    debounce?: number
}

const useSearchBarWithDebounceLogic = ({ searchValue, onChange, debounce = 150 }: SearchBarWithDebounceLogicProps) => {
    const [mySearchValue, setMySearchValue] = useState<string>(searchValue)
    const [debounceValue] = useDebounce(mySearchValue, debounce)

    const onReset = useCallback(() => {
        onChange('')
        setMySearchValue('')
    }, [onChange, setMySearchValue])

    const { searchIcon, resetSearchIcon } = useResetSearchValueLogic({ searchValue: debounceValue, onChange: onReset })


    useEffect(() => {
        setMySearchValue(searchValue)
    }, [searchValue])

    useEffect(() => {
        onChange(debounceValue)
    }, [debounceValue, onChange])
    return { mySearchValue, setMySearchValue, searchIcon, resetSearchIcon }
}

interface SearchBarCheckBoxProps extends SearchBarProps {
    checked: boolean
    onChangeCheckbox?: (value: boolean) => void
    label?: string
    readOnly?: boolean
}

export const SearchBarCheckBox: React.FC<SearchBarCheckBoxProps> = ({ searchValue, className, label, onChangeCheckbox, checked, readOnly, onChange, ...rest }) => {
    const { searchIcon, resetSearchIcon } = useResetSearchValueLogic({ searchValue, onChange })
    return (
        <div className={' light14 searchbar flex border-b-green-21 ' + (className || '')} onClick={(e) => e.stopPropagation()}>
            <Checkbox className='ml-21' label={label || ''} onChange={onChangeCheckbox} {...{ checked, readOnly }} />
            <InputWithRightIcon className='placeholder-gray-97 w-full ' iconStyles={{ height: '16px', width: '16px', color: '#636363' }} icon={searchIcon} value={searchValue} onChange={onChange} onClickResetIcon={resetSearchIcon} {...rest} />
        </div>
    )
}

interface ResetSearchValueLogicProps {
    searchValue: string
    onChange: ChangeValueFunc
}

const useResetSearchValueLogic = ({ searchValue, onChange }: ResetSearchValueLogicProps) => {
    const searchIcon = searchValue ? faXmark : faSearch

    const resetSearchIcon = useCallback(() => {
        if (searchValue) {
            onChange('')
        }
    }, [onChange, searchValue])

    return { searchIcon, resetSearchIcon }
}

export default SearchBar